import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { RiDeleteBinLine } from '@remixicon/react'
import { Button } from '@/components/ui/button'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { api } from '@/lib/api'

export function PlaylistDangerZone({
	playlistId,
	playlistName,
}: {
	playlistId: string
	playlistName: string
}) {
	const navigate = useNavigate()
	const queryClientHook = useQueryClient()
	const [deleteOpen, setDeleteOpen] = useState(false)

	const deleteMutation = useMutation({
		mutationFn: async () => {
			const response = await api.api.playlists({ id: playlistId }).delete()
			if (response.error) {
				throw new Error('Failed to delete playlist')
			}
			return response.data
		},
		onSuccess: () => {
			queryClientHook.invalidateQueries({ queryKey: ['playlists'] })
			navigate({ to: '/' })
		},
	})

	return (
		<div className="mt-16 border-t border-border/20 pb-12 pt-8">
			<div className="flex items-center justify-between">
				<div>
					<p className="text-xs font-medium text-muted-foreground/60">Danger zone</p>
					<p className="mt-0.5 text-[11px] text-muted-foreground/30">
						Permanently delete this playlist and all its entries.
					</p>
				</div>
				<AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
					<AlertDialogTrigger asChild>
						<Button
							variant="destructive"
							size="sm"
							disabled={deleteMutation.isPending}
						>
							<RiDeleteBinLine className="size-3.5" />
							Delete Playlist
						</Button>
					</AlertDialogTrigger>
					<AlertDialogContent onOverlayClick={() => setDeleteOpen(false)}>
						<AlertDialogHeader>
							<AlertDialogTitle>Delete playlist</AlertDialogTitle>
							<AlertDialogDescription>
								This will permanently delete "{playlistName}" and all its entries. This action cannot be undone.
							</AlertDialogDescription>
						</AlertDialogHeader>
						<AlertDialogFooter>
							<AlertDialogCancel>Cancel</AlertDialogCancel>
							<AlertDialogAction
								onClick={() => deleteMutation.mutate()}
								className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
							>
								Delete
							</AlertDialogAction>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialog>
			</div>
		</div>
	)
}
